import React from 'react';
import { IonContent, IonIcon, IonPage, IonRefresher, IonRefresherContent } from '@ionic/react';
import { arrowBack, star, starOutline } from 'ionicons/icons';
import { useHistory, useParams } from 'react-router-dom';
import { StoreTypeTag, YoYBadge } from '../components';
import { useStoreDetail } from '../hooks/useSuQueries';
import { useFavourites } from '../hooks/useFavourites';
import { PERIODS, PERIOD_LABELS } from '../utils/periods';
import { formatMoney, moneyLabel, MONEY_UNIT, formatPct, yoyTone } from '../utils/format';
import { formatAge } from './detail/formatAge';
import './stores/stores.css';
import './detail/detail.css';

const StoreDetailPage: React.FC = () => {
  const { siteCode } = useParams<{ siteCode: string }>();
  const history = useHistory();
  const { data, loading, error, refetch } = useStoreDetail(siteCode);
  const { isFavourite, toggle } = useFavourites();
  const fav = isFavourite(siteCode);
  const notFound = (error as any)?.response?.status === 404;

  const back = () => (history.action !== 'POP' ? history.goBack() : history.replace('/app/stores'));
  const onRefresh = (e: CustomEvent) => {
    refetch();
    setTimeout(() => (e.target as any).complete(), 600);
  };

  const today = data?.performance.today;
  const lfl = data?.lfl_growth;
  const lflValue = lfl?.yoy_pct ?? null;
  const lflNote = lflValue != null ? null : data?.is_lfl === false ? 'Not an LFL store' : lfl === null && data?.is_lfl == null ? 'Coming soon' : null;
  const lflText = (v: boolean | null | undefined) => (v == null ? '–' : v ? 'Yes' : 'No');

  return (
    <IonPage>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>
        <div className="su-detail-head">
          <button type="button" className="su-icon-btn" aria-label="Back" onClick={back}>
            <IonIcon icon={arrowBack} />
          </button>
          <div className="titles">
            <h1>{data?.store_name ?? (notFound ? 'Store' : siteCode)}</h1>
            {data && <div className="sub">{data.city} | {data.state} | {data.store_type === 'NEW_STORE' ? 'NEW' : data.store_type}</div>}
          </div>
          <button
            type="button"
            className="su-icon-btn"
            aria-label={fav ? 'Remove from favourites' : 'Add to favourites'}
            aria-pressed={fav}
            onClick={() => toggle(siteCode)}
          >
            <IonIcon icon={fav ? star : starOutline} className={fav ? 'su-star-on' : ''} />
          </button>
        </div>

        <div className="su-page" style={{ paddingTop: 0 }}>
          {error && !data ? (
            <div className="su-card su-error">
              <div>{notFound ? 'Store not found' : 'Could not load store details.'}</div>
              {notFound ? (
                <button type="button" className="su-btn" onClick={() => history.replace('/app/stores')}>Back to Stores</button>
              ) : (
                <button type="button" className="su-btn" onClick={refetch}>Retry</button>
              )}
            </div>
          ) : loading && !data ? (
            <>
              <div className="su-detail-cards">
                <div className="su-skeleton" style={{ height: 110 }} />
                <div className="su-skeleton" style={{ height: 110 }} />
              </div>
              <div className="su-skeleton" style={{ height: 180 }} />
              <div className="su-skeleton" style={{ height: 200 }} />
            </>
          ) : data && today ? (
            <>
              <div className="su-detail-cards">
                <div className="su-card su-kpi">
                  <div className="su-kpi-title"><span>Today's Sales ({moneyLabel()})</span></div>
                  <div className="su-kpi-value su-num">
                    {formatMoney(today.sales)}
                    <span className="su-kpi-unit">{MONEY_UNIT.key}</span>
                  </div>
                  <div className="su-kpi-row" style={{ flexWrap: 'wrap', gap: 4 }}>
                    <YoYBadge value={today.yoy_pct} variant="inline" isNew={data.store_type === 'NEW_STORE'} />
                    <span className="su-num">vs LY {formatMoney(today.ly_sales)}</span>
                  </div>
                </div>
                <div className="su-card su-kpi">
                  <div className="su-kpi-title"><span>LFL Growth</span></div>
                  <div className={`su-kpi-value su-num su-${yoyTone(lflValue)}`}>{formatPct(lflValue)}</div>
                  <div className="su-kpi-row" style={{ flexWrap: 'wrap', gap: 4 }}>
                    <span>{lflNote ?? ''}</span>
                    <span>LFL Stores</span>
                  </div>
                </div>
              </div>

              <div className="su-card su-perf">
                <h3 className="su-h2" style={{ marginBottom: 8 }}>Sales Performance ({moneyLabel()})</h3>
                <table className="su-table su-perf-table">
                  <thead>
                    <tr><th /><th className="num">Sales</th><th className="num">LY Sales</th><th className="num">YoY %</th></tr>
                  </thead>
                  <tbody>
                    {PERIODS.map((p) => {
                      const r = data.performance[p];
                      return (
                        <tr key={p}>
                          <td>{PERIOD_LABELS[p]}</td>
                          <td className="num su-num">{formatMoney(r?.sales)}</td>
                          <td className="num su-num">{r && r.ly_sales > 0 ? formatMoney(r.ly_sales) : '-'}</td>
                          <td className="num su-num"><YoYBadge value={r?.yoy_pct} variant="inline" showArrow={false} isNew={data.store_type === 'NEW_STORE'} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="su-card">
                <h3 className="su-h2" style={{ marginBottom: 4 }}>Store Information</h3>
                <div className="su-info">
                  <div>Store Type</div><div><StoreTypeTag type={data.store_type} /></div>
                  <div>State</div><div>{data.state || '–'}</div>
                  <div>City</div><div>{data.city || '–'}</div>
                  <div>LFL Store</div><div>{lflText(data.is_lfl)}</div>
                  <div>Store Age</div><div className="su-num">{formatAge(data.age_months)}</div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default StoreDetailPage;
